'use client'

import { useEffect, useState, useCallback, useRef } from 'react';

// Type definitions for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
          }) => void;
          prompt: (callback?: (notification: PromptMomentNotification) => void) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: string;
              locale?: string;
            }
          ) => void;
        };
      };
    };
  }
}

export interface GoogleCredentialResponse {
  credential: string; // This is the id_token (JWT)
  select_by: 'auto' | 'user' | 'user_1tap' | 'user_2tap' | 'btn' | 'btn_confirm' | 'brn_add_session' | 'btn_confirm_add_session';
}

export interface PromptMomentNotification {
  isNotDisplayed: boolean;
  isSkippedMoment: boolean;
  isDismissedMoment: boolean;
  getMomentType: () => 'display' | 'skipped' | 'dismissed';
  getNotDisplayedReason: () => 'browser_not_supported' | 'invalid_client' | 'missing_client_id' | 'opt_out_or_no_session' | 'secure_http_required' | 'suppressed_by_user' | 'unregistered_origin' | 'unknown_reason';
  getSkippedReason: () => 'auto_cancel' | 'user_cancel' | 'tap_outside' | 'issuing_failed';
  getDismissedReason: () => 'credential_returned' | 'cancel_called' | 'flow_restarted';
}

interface UseGoogleAuthReturn {
  isGoogleLoaded: boolean;
  signInWithGoogle: () => Promise<string | null>; // Returns id_token or null
  error: string | null;
}

/**
 * Hook for Google Identity Services authentication
 * 
 * This hook loads Google Identity Services SDK and provides a function
 * to trigger Google sign-in and receive the id_token.
 * 
 * @returns {UseGoogleAuthReturn} Object containing:
 * - isGoogleLoaded: boolean indicating if Google SDK is loaded
 * - signInWithGoogle: function to trigger Google sign-in and return id_token
 * - error: any error message
 * 
 * @example
 * const { isGoogleLoaded, signInWithGoogle, error } = useGoogleAuth();
 * 
 * const handleGoogleLogin = async () => {
 *   try {
 *     const idToken = await signInWithGoogle();
 *     if (idToken) {
 *       // Send idToken to backend
 *       await loginWithGoogle(idToken);
 *     }
 *   } catch (err) {
 *     console.error('Google sign-in failed:', err);
 *   }
 * };
 */
export const useGoogleAuth = (): UseGoogleAuthReturn => {
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const credentialResolverRef = useRef<((value: string | null) => void) | null>(null);

  // Load Google Identity Services SDK
  useEffect(() => {
    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

    if (!clientId) {
      setError('Google Client ID is not configured');
      return;
    }

    // Check if already loaded
    if (window.google?.accounts?.id) {
      setIsGoogleLoaded(true);
      // Initialize with callback
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: GoogleCredentialResponse) => {
          if (credentialResolverRef.current) {
            credentialResolverRef.current(response.credential);
            credentialResolverRef.current = null;
          }
        },
      });
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        setIsGoogleLoaded(true);
        setError(null);
        // Initialize with callback
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: GoogleCredentialResponse) => {
            if (credentialResolverRef.current) {
              credentialResolverRef.current(response.credential);
              credentialResolverRef.current = null;
            }
          },
        });
      } else {
        setError('Failed to load Google Identity Services');
      }
    };
    script.onerror = () => {
      setError('Failed to load Google Identity Services script');
    };

    document.head.appendChild(script);

    // Cleanup
    return () => {
      // Don't remove script on cleanup to avoid reloading
    };
  }, []);

  /**
   * Trigger Google Sign-In and return id_token
   * 
   * This function creates a temporary hidden Google button, triggers it,
   * and returns the id_token via the callback.
   * 
   * @returns {Promise<string | null>} Promise that resolves with id_token or null
   */
  const signInWithGoogle = useCallback((): Promise<string | null> => {
    return new Promise((resolve, reject) => {
      const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

      if (!clientId) {
        reject(new Error('Google Client ID is not configured'));
        return;
      }

      if (!window.google?.accounts?.id) {
        reject(new Error('Google Identity Services is not loaded'));
        return;
      }

      // Set up resolver
      credentialResolverRef.current = resolve;

      // Create a temporary hidden container for the button
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.top = '-9999px';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '1px';
      tempContainer.style.height = '1px';
      tempContainer.style.overflow = 'hidden';
      document.body.appendChild(tempContainer);

      // Render Google button in the hidden container
      window.google.accounts.id.renderButton(tempContainer, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        width: '200px',
      });

      // Find and click the button
      setTimeout(() => {
        const button = tempContainer.querySelector('div[role="button"]') as HTMLElement;
        if (button) {
          button.click();
        } else {
          // Fallback: if button not found, try prompt
          window.google?.accounts?.id.prompt((notification) => {
            if (notification.isNotDisplayed) {
              const reason = notification.getNotDisplayedReason();
              credentialResolverRef.current = null;
              document.body.removeChild(tempContainer);
              reject(new Error(`Google sign-in not displayed: ${reason}`));
            } else if (notification.isSkippedMoment) {
              const reason = notification.getSkippedReason();
              credentialResolverRef.current = null;
              document.body.removeChild(tempContainer);
              reject(new Error(`Google sign-in skipped: ${reason}`));
            } else if (notification.isDismissedMoment) {
              const reason = notification.getDismissedReason();
              if (reason !== 'credential_returned') {
                credentialResolverRef.current = null;
                document.body.removeChild(tempContainer);
                reject(new Error(`Google sign-in dismissed: ${reason}`));
              }
            }
          });
        }

        // Cleanup after timeout
        setTimeout(() => {
          if (document.body.contains(tempContainer)) {
            document.body.removeChild(tempContainer);
          }
          if (credentialResolverRef.current) {
            credentialResolverRef.current = null;
            reject(new Error('Google sign-in timeout'));
          }
        }, 60000); // 60 second timeout
      }, 100);
    });
  }, [isGoogleLoaded]);

  return {
    isGoogleLoaded,
    signInWithGoogle,
    error,
  };
};
